// Grava uma nota de voz no navegador e devolve o arquivo pronto para subir.
//
// Mora em `shared/` porque o Playground do agente e o widget já importam daqui, e os dois
// precisam do mesmo gravador. Não é o `WootWriter/AudioRecorder.vue` de propósito: aquele
// converte com lamejs, chama todo arquivo de `.mp3` e não tem teto. Aqui o arquivo sai no
// formato que o navegador gravou, com o MIME e a extensão verdadeiros, porque é o MIME que
// escolhe o decodificador no servidor (`Messages::AudioTranscriptionService#extensao`), e um
// `video/webm` viraria vídeo no `FileTypeHelper`, nunca áudio.
//
// Enquanto grava, as ondas (A-13): o nível do som lido por um `AnalyserNode` do mesmo fluxo que o
// MediaRecorder grava, sem biblioteca. É o que mostra, enquanto se fala, que o microfone está
// captando. O widget usa este gravador, e ganha as ondas junto.
import { ref, getCurrentInstance, onBeforeUnmount } from 'vue';

// Nota de voz, não palestra: no teto a gravação para sozinha e o arquivo segue como está.
export const DURACAO_MAXIMA_MS = 2 * 60 * 1000;
// Abaixo disto é toque sem querer no botão: não vira arquivo, nem envio, nem transcrição paga.
export const DURACAO_MINIMA_MS = 1000;

// O que se pede ao MediaRecorder, na ordem. Chrome, Edge e Firefox gravam WebM com Opus; o
// Safari grava MP4.
const FORMATOS = ['audio/webm;codecs=opus', 'audio/mp4'];

// Quantas barras as ondas têm, e de quanto em quanto tempo entra a mais nova, à direita.
export const BARRAS = 24;
const AMOSTRA_MS = 100;

// O nível de um trecho do som, de 0 a 1: a raiz quadrática média das amostras (bytes em torno de
// 128), numa curva de raiz, para a fala baixa também mexer a barra.
export function nivelDoSom(amostras) {
  if (!amostras.length) return 0;
  let soma = 0;
  amostras.forEach(amostra => {
    const x = (amostra - 128) / 128;
    soma += x * x;
  });
  return Math.min(1, Math.sqrt(Math.sqrt(soma / amostras.length)) * 1.6);
}

function formatoDoNavegador() {
  if (typeof window === 'undefined' || !window.MediaRecorder) return null;
  // Sem `mediaDevices` não há microfone: é o caso de página servida sem HTTPS.
  if (!navigator.mediaDevices?.getUserMedia) return null;
  return FORMATOS.find(f => window.MediaRecorder.isTypeSupported(f)) || null;
}

// O tipo e a extensão do arquivo, a partir do que o gravador diz ter gravado. Quem decide é o
// contêiner, e o tipo sai sempre `audio/*`, mesmo quando o navegador rotula `video/webm`.
// `.m4a` é o MP4 só de áudio com AAC, que é o que o Safari grava; outro codec no mesmo
// contêiner (o Chrome grava Opus em MP4) fica `.mp4`, que não promete AAC a ninguém.
export function arquivoDeAudio(mime) {
  const tipo = (mime || '').toLowerCase();
  if (tipo.includes('webm')) return { tipo: 'audio/webm', extensao: 'webm' };
  if (tipo.includes('mp4')) {
    const aac = !tipo.includes('codecs') || /mp4a|aac/.test(tipo);
    return { tipo: 'audio/mp4', extensao: aac ? 'm4a' : 'mp4' };
  }
  return null;
}

export function useGravadorDeAudio() {
  const formato = formatoDoNavegador();
  // Se o navegador grava num dos dois formatos. Sem isto a tela nem oferece o botão.
  const podeGravar = formato !== null;
  const gravando = ref(false);
  const decorridoMs = ref(0);
  // `microfone`: o navegador não deu o microfone (permissão negada, nenhum dispositivo).
  // `gravador`: o MediaRecorder falhou ou não entregou nada. Nos dois, `iniciar` devolve null,
  // e a tela pode dizer o porquê em vez de o botão não fazer nada.
  const erro = ref(null);
  // As ondas: os últimos BARRAS níveis, de 0 a 1, o mais novo no fim. Vazio fora da gravação, e
  // no navegador sem AudioContext, que grava sem ondas.
  const niveis = ref([]);

  let gravador = null;
  let fluxo = null;
  let contexto = null;
  let analisador = null;
  let amostrador = null;
  let pedacos = [];
  let inicio = 0;
  let relogio = null;
  let teto = null;
  let pedindo = false;
  // O pedido do microfone que vale. O `cancelar` feito enquanto o navegador pede passa a vez, e o
  // pedido que ficou para trás, quando o navegador responder, não mexe mais em nada.
  let pedidoDaVez = 0;
  let descartar = false;
  let concluir = null;

  // O Safari só deixa o contexto de áudio rodar quando ele nasce no gesto de quem clicou: isto roda
  // antes do primeiro `await` de `iniciar`, ainda dentro do clique. Sem AudioContext, ou se ele
  // falhar, a gravação segue sem ondas.
  function abrirContexto() {
    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!Contexto) return;
    try {
      contexto = new Contexto();
      if (contexto.state === 'suspended') contexto.resume().catch(() => {});
    } catch (e) {
      contexto = null;
    }
  }

  // O analisador lê o mesmo fluxo que o gravador grava. Ele não vai para a saída de som: ouvir o
  // próprio microfone seria eco.
  function ligarAnalisador() {
    if (!contexto) return;
    try {
      analisador = contexto.createAnalyser();
      analisador.fftSize = 1024;
      contexto.createMediaStreamSource(fluxo).connect(analisador);
    } catch (e) {
      analisador = null;
    }
  }

  function amostrar() {
    const amostras = new Uint8Array(analisador.fftSize);
    analisador.getByteTimeDomainData(amostras);
    niveis.value = [...niveis.value.slice(1), nivelDoSom(amostras)];
  }

  // Tudo o que a gravação abriu fecha junto: o iOS tem teto de contextos de áudio abertos.
  function soltarMicrofone() {
    clearInterval(relogio);
    clearTimeout(teto);
    clearInterval(amostrador);
    if (fluxo) fluxo.getTracks().forEach(trilha => trilha.stop());
    if (contexto) contexto.close().catch(() => {});
    fluxo = null;
    gravador = null;
    contexto = null;
    analisador = null;
    niveis.value = [];
    gravando.value = false;
  }

  function terminar(resultado) {
    const fim = concluir;
    concluir = null;
    soltarMicrofone();
    if (fim) fim(resultado);
  }

  function aoParar() {
    // O `stop` também chega depois de um `error`, que já terminou a gravação.
    if (!concluir) return;
    // Parada antes do `start` não gravou nada.
    const duracaoMs = inicio
      ? Math.min(Date.now() - inicio, DURACAO_MAXIMA_MS)
      : 0;
    if (descartar || duracaoMs < DURACAO_MINIMA_MS) {
      terminar(null);
      return;
    }
    // O Firefox já entregou `stop` sem dado nenhum, sobrando de uma gravação anterior.
    if (!pedacos.length) {
      erro.value = 'gravador';
      terminar(null);
      return;
    }
    const { tipo, extensao } =
      arquivoDeAudio(gravador.mimeType) || arquivoDeAudio(formato);
    const arquivo = new File(pedacos, `nota-de-voz-${Date.now()}.${extensao}`, {
      type: tipo,
    });
    terminar({ arquivo, duracaoMs });
  }

  function parar() {
    if (gravador?.state === 'recording') gravador.stop();
  }

  function cancelar() {
    descartar = true;
    // Enquanto o navegador pede o microfone ainda não há gravador para parar. O Firefox deixa o
    // pedido pendente para sempre quando se fecha a pergunta clicando fora: sem liberar a vaga
    // aqui, o clique seguinte recebia null na hora, sem pedir o microfone nem avisar nada.
    if (pedindo) {
      pedindo = false;
      pedidoDaVez += 1;
      soltarMicrofone();
      return;
    }
    parar();
  }

  // "Gravando" só depois de o gravador começar de fato (A-13): antes disto o que se fala não entra
  // no arquivo, e a tela não pode convidar a falar. O relógio, o teto e as ondas contam daqui.
  function comecou() {
    inicio = Date.now();
    decorridoMs.value = 0;
    gravando.value = true;
    relogio = setInterval(() => {
      decorridoMs.value = Date.now() - inicio;
    }, 250);
    teto = setTimeout(parar, DURACAO_MAXIMA_MS);
    if (analisador) {
      niveis.value = Array(BARRAS).fill(0);
      amostrador = setInterval(amostrar, AMOSTRA_MS);
    }
  }

  // Grava até `parar`, `cancelar` ou o teto, e resolve com `{ arquivo, duracaoMs }` — ou com
  // null quando não há o que enviar: cancelada, curta demais, ou sem microfone. Quem chama o faz no
  // clique, sem `await` antes: o contexto das ondas nasce aqui, dentro do gesto.
  async function iniciar() {
    // Uma gravação por vez: do pedido do microfone até o fim, e não só enquanto "gravando".
    if (!podeGravar || pedindo || concluir) return null;
    erro.value = null;
    descartar = false;
    pedindo = true;
    pedidoDaVez += 1;
    const pedido = pedidoDaVez;
    abrirContexto();
    let recebido;
    try {
      recebido = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      // A recusa de um pedido já cancelado não é desta vez: o contexto e o aviso são de outro clique.
      if (pedido !== pedidoDaVez) return null;
      erro.value = 'microfone';
      soltarMicrofone();
      return null;
    } finally {
      if (pedido === pedidoDaVez) pedindo = false;
    }
    // Cancelada, ou a tela saiu, enquanto o navegador pedia o microfone: o microfone que chega
    // depois volta na hora. Sem isto a gravação começaria com a luz do microfone acesa e ninguém
    // para pará-la, e o fluxo atrasado tomaria o lugar do da gravação de um clique mais novo.
    if (pedido !== pedidoDaVez) {
      recebido.getTracks().forEach(trilha => trilha.stop());
      return null;
    }
    fluxo = recebido;
    return new Promise(resolve => {
      concluir = resolve;
      pedacos = [];
      inicio = 0;
      try {
        const este = new window.MediaRecorder(fluxo, { mimeType: formato });
        gravador = este;
        gravador.ondataavailable = evento => {
          if (evento.data?.size) pedacos.push(evento.data);
        };
        gravador.onstop = aoParar;
        gravador.onerror = () => {
          erro.value = 'gravador';
          terminar(null);
        };
        // O `start` que chega depois de a gravação acabar (parada ou cancelada antes dele) não
        // acende mais nada.
        gravador.onstart = () => {
          if (gravador === este) comecou();
        };
        ligarAnalisador();
        gravador.start();
      } catch (e) {
        erro.value = 'gravador';
        terminar(null);
      }
    });
  }

  // Quem sai da tela no meio da gravação não deixa o microfone aceso.
  if (getCurrentInstance()) onBeforeUnmount(cancelar);

  return {
    podeGravar,
    gravando,
    decorridoMs,
    niveis,
    erro,
    iniciar,
    parar,
    cancelar,
  };
}
